import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { getNonSubmittedApplications, getSubmittedApplications, getBlockedApplications, getAdminStatistics, getDashboardCountsByLabType, extendApplication, getAdminFilterOptions, getAllFilteredApplications } from '../../services/api';
import { LoaderWrapper } from '../../components/index';
import * as XLSX from 'xlsx';
import { LAB_TYPES, getLabTypeName, getFirstLabTypeId } from '../../utils/labTypeFilter';
import { encryptAppId, encryptText } from '../../utils/encryption';
import { useNavigate } from 'react-router-dom';

// Configure dayjs
dayjs.extend(customParseFormat);

const AdminDashboard = () => {
  // Utility functions for lab type management (similar to CategoryManagement)
  const getAllowedLabTypes = () => {
    const labTypeIds = localStorage.getItem('labTypeIds');
    const labTypeId = localStorage.getItem('labTypeId');

    if (!labTypeIds && !labTypeId) {
      return [];
    }

    const allowedTypes = [];
    const typeIds = labTypeIds ? labTypeIds.split(',') : [labTypeId];

    typeIds.forEach(id => {
      switch (id.trim()) {
        case '1':
          allowedTypes.push('Testing');
          break;
        case '3':
          allowedTypes.push('Medical');
          break;
        case '2':
          allowedTypes.push('Calibration');
          break;
        case '4':
          allowedTypes.push('PTP');
          break;
        case '5':
          allowedTypes.push('RMP');
          break;
      }
    });

    return allowedTypes;
  };

  const shouldDisableLabTypeDropdown = () => {
    return getAllowedLabTypes().length <= 1;
  };

  const getDefaultLabType = () => {
    const allowedTypes = getAllowedLabTypes();
    return allowedTypes.length === 1 ? allowedTypes[0] : null;
  };

  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [currentTab, setCurrentTab] = useState(1); // 1: Submitted, 2: Non-Submitted
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [labTypeFilter, setLabTypeFilter] = useState(getDefaultLabType());
  const [assessmentTypeFilter, setAssessmentTypeFilter] = useState(null);
  const [usernameFilter, setUsernameFilter] = useState(null);
  const [labIdFilter, setLabIdFilter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  // Options for dropdowns
  const [labTypeOptions, setLabTypeOptions] = useState([]);
  const [assessmentTypeOptions, setAssessmentTypeOptions] = useState([]);
  const [usernameOptions, setUsernameOptions] = useState([]);
  const [labIdOptions, setLabIdOptions] = useState([]);

  const [stats, setStats] = useState({
    submitted: 0,
    nonSubmitted: 0,
    blocked: 0,
    total: 0,
    underReview: 0,
    scrutiny: 0,
    pendingReview: 0,
    finance: '0.00',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);

  // Extend Date Dialog State
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [selectedExtendApp, setSelectedExtendApp] = useState(null);
  const [extendedDate, setExtendedDate] = useState(null);
  const [extendedRemarks, setExtendedRemarks] = useState('');
  const [extending, setExtending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [currentTab, labTypeFilter, assessmentTypeFilter, usernameFilter, labIdFilter, debouncedSearchText, page, rowsPerPage]);

  // Fetch filter options only once on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [currentTab, labTypeFilter, assessmentTypeFilter, usernameFilter, labIdFilter, debouncedSearchText]);

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    // For server-side pagination, we don't need this local filtering anymore
    // Just set applications data directly
    setFilteredApplications(applications);
    // Note: totalCount is now set from API response, not applications.length
  }, [applications]);

  const fetchFilterOptions = async () => {
    try {
      setFilterLoading(true);

      // Fetch all filter options from database
      const [labTypesRes, assessmentTypesRes, usernamesRes, labIdsRes] = await Promise.all([
        getAdminFilterOptions('labtypes'),
        getAdminFilterOptions('assessmenttypes'),
        getAdminFilterOptions('usernames'),
        getAdminFilterOptions('labids')
      ]);

      // Process lab types with allowed types filtering
      let allLabTypes = [];
      if (labTypesRes.data && labTypesRes.data.success) {
        allLabTypes = labTypesRes.data.data || [];
        const allowedLabTypes = getAllowedLabTypes();
        const filteredLabTypes = allowedLabTypes.length > 0
          ? allLabTypes.filter(labType => allowedLabTypes.includes(labType))
          : allLabTypes;
        setLabTypeOptions(filteredLabTypes);
      }

      // Set other filter options
      if (assessmentTypesRes.data && assessmentTypesRes.data.success) {
        const assessmentTypes = assessmentTypesRes.data.data || [];
        setAssessmentTypeOptions(assessmentTypes);
        console.log('Assessment types loaded:', assessmentTypes); // Debug log
      }

      if (usernamesRes.data && usernamesRes.data.success) {
        setUsernameOptions(usernamesRes.data.data || []);
      }

      if (labIdsRes.data && labIdsRes.data.success) {
        setLabIdOptions(labIdsRes.data.data || []);
      }

    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Set empty arrays as fallback
      setLabTypeOptions([]);
      setAssessmentTypeOptions([]);
      setUsernameOptions([]);
      setLabIdOptions([]);
    } finally {
      setFilterLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);

      // Determine which API to call based on current tab
      // Build flexible filters: support objects with id or plain strings. For lab type try to map name -> id using LAB_TYPES
      const resolveLabTypeParam = () => {
        if (!labTypeFilter) return '';
        if (typeof labTypeFilter === 'object' && labTypeFilter.id) return labTypeFilter.id;
        // try to match name to known LAB_TYPES
        const match = LAB_TYPES.find(l => String(l.name).toLowerCase() === String(labTypeFilter).toLowerCase());
        return match ? match.id : String(labTypeFilter);
      };

      const resolveAssessmentTypeParam = () => {
        if (!assessmentTypeFilter) return '';
        if (typeof assessmentTypeFilter === 'object' && assessmentTypeFilter.id) {
          console.log('Sending assessmentTypeId:', assessmentTypeFilter.id, 'for:', assessmentTypeFilter.name); // Debug log
          return assessmentTypeFilter.id; // Return the ID for API call
        }
        console.log('Sending assessmentType as string:', assessmentTypeFilter); // Debug log
        return String(assessmentTypeFilter); // Fallback to string value
      };

      const filters = {
        labTypeId: resolveLabTypeParam(),
        userName: usernameFilter || '',
        assessmentTypeId: resolveAssessmentTypeParam(),
        labId: labIdFilter || '',
        search: debouncedSearchText || '',
        page: page + 1, // Backend uses 1-based pagination
        limit: rowsPerPage
      };

      let response;
      if (currentTab === 1) {
        // Fetch submitted applications (Mode 67)
        response = await getSubmittedApplications(filters);
      } else if (currentTab === 2) {
        // Fetch non-submitted applications (Mode 2)
        response = await getNonSubmittedApplications(filters);
      } else if (currentTab === 3) {
        // Fetch blocked applications (where isblock = 1)
        response = await getBlockedApplications(filters);
      }

      if (response && response.data && (response.data.success === true || response.data.status === 'success')) {
        // support different response shapes (legacy)
        const apps = response.data.data || response.data || [];
        setApplications(Array.isArray(apps) ? apps : []);

        // Set totalCount from API response for server-side pagination
        if (response.data.totalCount !== undefined) {
          setTotalCount(response.data.totalCount);
        } else {
          // Fallback for legacy responses
          setTotalCount(Array.isArray(apps) ? apps.length : 0);
        }
      }

      // Fetch statistics using stored procedure (Mode 5 for Medical)
      const statsResponse = await getAdminStatistics();

      if (statsResponse.data.success) {
        const statsData = statsResponse.data.data;
        setStats({
          submitted: statsData.submitted || 0,
          nonSubmitted: statsData.nonSubmitted || 0,
          total: statsData.total || 0,
          blocked: statsData.blocked || 0,
          underReview: statsData.underReview || 0,
          scrutiny: statsData.scrutiny || 0,
          pendingReview: statsData.pendingReview || 0,
          finance: statsData.finance || '0.00',
        });
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClearFilters = () => {
    setLabTypeFilter(getDefaultLabType());
    setAssessmentTypeFilter(null);
    setUsernameFilter(null);
    setLabIdFilter(null);
    setSearchText('');
    setDebouncedSearchText('');
  };

  const handleMenuOpen = (event, app) => {
    setAnchorEl(event.currentTarget);
    setSelectedApp(app);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedApp(null);
  };

  const navigate = useNavigate();

  const handleViewDetails = (app) => {
    // Navigate to application details or open modal
    console.log('View details for:', app);
    handleMenuClose();
  };

  const handleBlockApplication = async (app) => {
    // Call API to block application
    console.log('Block application:', app);
    handleMenuClose();
    // Refresh data after action
    fetchApplications();
  };

  const handleExtendApplication = (app) => {
    setSelectedExtendApp(app);
    setExtendDialogOpen(true);
    setExtendedDate(null);
    setExtendedRemarks('');
  };

  const handleExtendDialogClose = () => {
    setExtendDialogOpen(false);
    setSelectedExtendApp(null);
    setExtendedDate(null);
    setExtendedRemarks('');
  };

  const handleSubmitExtension = async () => {
    if (!extendedDate || !extendedRemarks.trim()) {
      setErrorMessage('Please select a date and provide remarks');
      return;
    }

    if (!extendedDate || !dayjs.isDayjs(extendedDate) || extendedDate.isBefore(dayjs(), 'day') || extendedDate.isSame(dayjs(), 'day')) {
      setErrorMessage('Extended date must be greater than today');
      return;
    }

    // Calculate days being extended (from today to selected date)
    const daysToExtend = extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day');

    // Get current extended days (default to 0 if not available)
    const currentExtendedDays = selectedExtendApp.extendedDays || 0;

    // Calculate total extended days after this extension
    const totalExtendedDays = currentExtendedDays + daysToExtend;

    // Check if total extended days would exceed 15 days limit
    if (totalExtendedDays > 15) {
      const remainingDays = 15 - currentExtendedDays;
      setErrorMessage(`Cannot extend beyond 15 days total. You have already extended ${currentExtendedDays} days. Maximum ${remainingDays} days can be extended.`);
      return;
    }

    try {
      setExtending(true);
      const response = await extendApplication({
        appId: selectedExtendApp.AppId,
        orgId: selectedExtendApp.OrgId,
        extendedDate: extendedDate.format('YYYY-MM-DD'), // Format as YYYY-MM-DD
        extendedRemarks: extendedRemarks.trim(),
        extendedBy: localStorage.getItem('orgId'),
        extendedDays: daysToExtend // Send the days being extended
      });

      if (response.data.success) {
        const { data: responseData } = response.data;
        let message = response.data.message || 'Application extended successfully!';
        if (responseData) {
          message = `Application extended successfully! Extended ${responseData.currentExtension} days. Total: ${responseData.totalExtendedDays}/15 days. Remaining: ${responseData.remainingDays} days.`;
        }
        setSuccessMessage(message);
        handleExtendDialogClose();
        fetchApplications(); // Refresh the data
      } else {
        setErrorMessage(response.data.message || 'Failed to extend application');
      }
    } catch (error) {
      console.error('Error extending application:', error);
      setErrorMessage('Error extending application: ' + (error.response?.data?.message || error.message));
    } finally {
      setExtending(false);
    }
  };

  const getStatusChip = (status, submitDate) => {
    if (status === 'Blocked') {
      return <Chip label="Blocked" color="error" size="small" icon={<BlockIcon />} />;
    } else if (status === 'Submitted' || submitDate) {
      return <Chip label="Submitted" color="success" size="small" icon={<CheckCircleIcon />} />;
    } else {
      return <Chip label="Non-Submitted" color="warning" size="small" icon={<HourglassEmptyIcon />} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  const exportToExcel = async () => {
    setExporting(true);

    try {
      // Define tab names and types
      const tabNames = {
        1: 'Submitted Applications',
        2: 'Non-Submitted Applications',
        3: 'Blocked Applications'
      };

      const tabTypes = {
        1: 'submitted',
        2: 'non-submitted',
        3: 'blocked'
      };

      // Prepare the same filters used for current display
      const resolveLabTypeParam = () => {
        if (!labTypeFilter) return '';
        if (typeof labTypeFilter === 'object' && labTypeFilter.id) return labTypeFilter.id;
        const match = LAB_TYPES.find(l => String(l.name).toLowerCase() === String(labTypeFilter).toLowerCase());
        return match ? match.id : String(labTypeFilter);
      };

      const resolveAssessmentTypeParam = () => {
        if (!assessmentTypeFilter) return '';
        if (typeof assessmentTypeFilter === 'object' && assessmentTypeFilter.id) {
          return assessmentTypeFilter.id;
        }
        return String(assessmentTypeFilter);
      };

      const filters = {
        labTypeId: resolveLabTypeParam(),
        userName: usernameFilter || '',
        assessmentTypeId: resolveAssessmentTypeParam(),
        labId: labIdFilter || '',
        search: debouncedSearchText || ''
      };

      // Fetch all filtered data (without pagination) for export
      console.log('Exporting with filters:', filters);
      const response = await getAllFilteredApplications(tabTypes[currentTab], filters);

      if (!response || !response.data || !response.data.success) {
        setErrorMessage('Failed to fetch data for export');
        return;
      }

      const allApplications = response.data.data || [];

      if (allApplications.length === 0) {
        setErrorMessage('No data available to export with current filters');
        return;
      }

      console.log(`Exporting ${allApplications.length} filtered applications`);

      // Prepare data for export
      const exportData = allApplications.map((app, index) => {
        const baseData = {
          'App Id': app.AppId || 'N/A',
          'Application No': app.ApplicationNo || 'N/A',
          'Laboratory Name': app.LaboratoryName || 'N/A',
          'Username': app.LoginName || 'N/A',
          'Lab Type': app.LabType || 'N/A',
          'Lab Type Id': app.LabTypeId || 'N/A',
          'Assessment Type': app.AssessmentType || 'N/A',
          'Assessment Type Id': app.AssessmentTypeId || 'N/A',
          'Standard': app.Standard || 'N/A',
          'Lab Size': app.category || 'N/A',
          'Discipline Name': app.disciplineName || 'N/A',
          'Contact Person': app.ContactPerson || 'N/A',
          'Contact Email': app.ContactEmail || 'N/A',
          'Contact Mobile': app.ContactMobile || 'N/A',
          'landline': app.landline || 'N/A',

          'Address': decodeHtmlEntities(app.Address) || 'N/A',
          'City': decodeHtmlEntities(app.City) || 'N/A',
          'State': decodeHtmlEntities(app.State) || 'N/A',
          'Pincode': app.Pincode || 'N/A',

          'Status': app.Status || 'N/A',
          'Stage': app.Stage || 'N/A',
          'Alloted': app.Alloted || 0,

          'DO Name': app.DOName || 'N/A',
          'LA Name': app.LAName || 'N/A',

          'Certificate No': app.CertificateNo || 'N/A',
          'Certificate Number': app.CertificateNumber || 'N/A',

          'Issue Date': formatDate(app.Issue_Date),
          'Valid Till': formatDate(app.ToDate),

          'Assessment Start Date': app.AssessmentStartDate || 'N/A',
          'Assessment End Date': app.AssessmentStopDate || 'N/A',

          'Created On': formatDate(app.InsertDate),
          'Submitted On': formatDate(app.SubmitDate),
          'Decision Date': formatDate(app.decisionDate),
          'Status Date': formatDate(app.statusDate),

          'Whether Extension Granted': app.WhetherExtensionGranted || 'N/A',
          'Viseye': app.Viseye || 0,
        };

        // Add tab-specific columns
        if (currentTab === 2) {
          baseData['Extended Date'] = app.ExtendedDate || 'N/A';
          baseData['Extended Remarks'] = app.extendedRemarks || 'N/A';
        } else if (currentTab === 3) {
          baseData['Block Date'] = formatDate(app.BlockDate);
        }

        return baseData;
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();

      // Set column widths for better readability
      const colWidths = [
        { wch: 8 },  // S. No
        { wch: 20 }, // Application No
        { wch: 30 }, // Laboratory Name
        { wch: 20 }, // Username
        { wch: 20 }, // Lab Type
        { wch: 20 }, // Assessment Type
        { wch: 25 }, // Contact Person
        { wch: 30 }, // Contact Email
        { wch: 15 }, // Contact Mobile
        { wch: 40 }, // Address
        { wch: 15 }, // City
        { wch: 15 }, // State
        { wch: 15 }, // Created On
      ];

      // Add additional column widths for tab-specific columns
      if (currentTab === 2) {
        colWidths.push({ wch: 15 }); // Extended Date
        colWidths.push({ wch: 30 }); // Extended Remarks
      } else if (currentTab === 3) {
        colWidths.push({ wch: 15 }); // Block Date
      }

      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, tabNames[currentTab]);

      // Generate filename with timestamp and filter info
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filterInfo = Object.entries(filters)
        .filter(([key, value]) => value && value !== '')
        .map(([key, value]) => `${key}-${value}`)
        .join('_');

      const baseFilename = tabNames[currentTab].replace(/ /g, '_');
      const filename = filterInfo
        ? `${baseFilename}_filtered_${filterInfo}_${timestamp}.xlsx`
        : `${baseFilename}_all_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      setSuccessMessage(`Excel file exported successfully: ${filename}. Total records: ${allApplications.length}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setErrorMessage('Failed to export Excel file: ' + (error.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };
  const viewApplication = (app) => {
    try {
      // Encrypt parameters similar to .NET Session variables
      const encryptedAppId = encryptAppId(app.AppId);
      const encryptedOrgId = encryptText(app.OrgId?.toString() || '');
      const encryptedLabTypeId = encryptText(app.LabTypeId?.toString() || '');
      const encryptedlaboratoryName = encryptText(app.LaboratoryName || '');

      console.log('Encrypted parameters:', {
        appId: encryptedAppId,
        orgId: encryptedOrgId,
        labTypeId: encryptedLabTypeId
      });

      // Build URL with encrypted parameters (equivalent to .NET Session["AppId"], Session["OrgIdCab"], Session["LabTypeId"])
      const queryParams = new URLSearchParams({
        appId: encryptedAppId,
        orgId: encryptedOrgId,
        labTypeId: encryptedLabTypeId,
        laboratoryName: encryptedlaboratoryName
      });

      const targetUrl = `/main/view/applications/view?${queryParams.toString()}`;

      // Navigate to ViewApplication page (equivalent to .NET Response.Redirect("Viewapplication"))
      navigate(targetUrl);
    } catch (err) {
      console.error('Navigate to view application error:', err);
    }
  };


  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading applications..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        Admin Dashboard
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2">Total Applications </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.submitted}
              </Typography>
              <Typography variant="body2">Submitted Applications</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.nonSubmitted}
              </Typography>
              <Typography variant="body2">Non-Submitted Applications</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.blocked}
              </Typography>
              <Typography variant="body2">Blocked Applications</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Applications Table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Submitted Applications" value={1} />
            <Tab label="Non-Submitted Applications" value={2} />
            {/* <Tab label="Blocked Applications" value={3} /> */}
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
              size="small"
              variant="outlined"
              color="primary"
              disabled={exporting || totalCount === 0}
              title={`Export all ${totalCount} filtered applications to Excel`}
            >
              {exporting ? 'Exporting...' : 'Export Records'}
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchApplications}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={labTypeOptions}
                value={labTypeFilter}
                onChange={(event, newValue) => setLabTypeFilter(newValue)}
                loading={filterLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lab Type"
                    placeholder="Type or select lab type"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
                disableClearable={false}
                disabled={shouldDisableLabTypeDropdown()}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={assessmentTypeOptions}
                value={assessmentTypeFilter}
                onChange={(event, newValue) => setAssessmentTypeFilter(newValue)}
                loading={filterLoading}
                getOptionLabel={(option) => {
                  // Handle both string and object options
                  if (typeof option === 'string') return option;
                  return option.name || option.label || option;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assessment Type"
                    placeholder="Type or select assessment type"
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  // Handle comparison between objects and strings
                  if (typeof option === 'string' && typeof value === 'string') {
                    return option === value;
                  }
                  if (typeof option === 'object' && typeof value === 'object') {
                    return option.id === value.id;
                  }
                  if (typeof option === 'object' && typeof value === 'string') {
                    return option.name === value || option.label === value;
                  }
                  if (typeof option === 'string' && typeof value === 'object') {
                    return option === value.name || option === value.label;
                  }
                  return false;
                }}
                clearOnEscape
                disableClearable={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={usernameOptions}
                value={usernameFilter}
                onChange={(event, newValue) => setUsernameFilter(newValue)}
                loading={filterLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Username"
                    placeholder="Type or select username"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
                disableClearable={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={labIdOptions}
                value={labIdFilter}
                onChange={(event, newValue) => setLabIdFilter(newValue)}
                loading={filterLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lab ID"
                    placeholder="Type or select lab ID"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
                disableClearable={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Search applications..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<FilterListIcon />}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchFilterOptions}
                  startIcon={<RefreshIcon />}
                  disabled={filterLoading}
                >
                  {filterLoading ? 'Refreshing...' : 'Refresh Filters'}
                </Button>
              </Box>
            </Grid>
            {currentTab === 3 && (
              <Grid item xs={12}>
                <Alert severity="error"> A maximum extension of 15 days will be allowed for the application.</Alert>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Results Counter */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="textSecondary">
            {loading ? 'Loading...' : `Showing ${(page * rowsPerPage) + 1}-${Math.min((page + 1) * rowsPerPage, totalCount)} of ${totalCount} applications`}
            {totalCount > rowsPerPage && ` (Page ${page + 1} of ${Math.ceil(totalCount / rowsPerPage)})`}
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>S. No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Laboratory Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Lab Info</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Contact Info</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created On</TableCell>
                {currentTab === 2 && (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>Extend Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Extend Remarks</TableCell>
                  </>
                )}
                {currentTab === 3 && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Block Date</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={currentTab === 3 ? 12 : 10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No applications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredApplications
                  .map((app, index) => (
                    <TableRow
                      key={app.AppId || index}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}<br />
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => viewApplication(app)}
                        >
                          View
                        </Button>
                        {currentTab === 3 && (
                          <Button color="primary" variant='contained' size="small" onClick={() => handleExtendApplication(app)}>
                            Extend Date
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>{app.LaboratoryName || app.OrgName || 'N/A'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {app.LoginName || 'N/A'}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {app.ApplicationNo && (
                              <Chip
                                label={app.ApplicationNo}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {app.LabType && (
                              <Chip
                                label={app.LabType}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {app.AssessmentType && (
                              <Chip
                                label={app.AssessmentType}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {app.ContactPerson || 'N/A'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(app.ContactEmail || app.Email) && (
                              <Chip
                                label={app.ContactEmail || app.Email}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            )}
                            {(app.ContactMobile || app.Phone) && (
                              <Chip
                                label={app.ContactMobile || app.Phone}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {app.Address && (
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              {decodeHtmlEntities(app.Address)}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {app.City && (
                              <Chip
                                label={decodeHtmlEntities(app.City)}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {app.State && (
                              <Chip
                                label={decodeHtmlEntities(app.State)}
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(app.InsertDate)}</TableCell>
                      {currentTab === 2 && (
                        <>
                          <TableCell>{app.ExtendedDate}</TableCell>
                          <TableCell>{app.extendedRemarks || 'N/A'}</TableCell>
                        </>
                      )}
                      {currentTab === 3 && (
                        <TableCell>{formatDate(app.BlockDate)}</TableCell>
                      )}

                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewDetails(selectedApp)}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleBlockApplication(selectedApp)}>
          <BlockIcon sx={{ mr: 1, fontSize: 20 }} />
          Extend date
        </MenuItem>
      </Menu>

      {/* Extend Date Dialog */}
      <Dialog
        open={extendDialogOpen}
        onClose={handleExtendDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'white',
            boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Extend Application Date
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            {selectedExtendApp?.LaboratoryName || selectedExtendApp?.OrgName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            {/* Extended Days Information */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Extension Limit Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Extended: ${selectedExtendApp?.extendedDays || 0} days`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Remaining: ${15 - (selectedExtendApp?.extendedDays || 0)} days`}
                    color={15 - (selectedExtendApp?.extendedDays || 0) > 0 ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label="Max: 15 days total"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Alert>
            </Grid>
            <Grid item xs={12} mt={1}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Extended Date"
                  value={extendedDate}
                  onChange={(newValue) => setExtendedDate(newValue)}
                  minDate={dayjs().add(1, 'day')} // Tomorrow onwards
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      helperText: "Select a date greater than today (dd/mm/yyyy)",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {/* Dynamic Extension Calculation */}
            {extendedDate && (
              <Grid item xs={12}>
                <Alert
                  severity={(() => {
                    const daysToExtend = extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day');
                    const currentExtendedDays = selectedExtendApp?.extendedDays || 0;
                    const totalDays = currentExtendedDays + daysToExtend;
                    return totalDays > 15 ? 'error' : totalDays > 3 ? 'warning' : 'success';
                  })()}
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Extension Calculation
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body2">
                      Extending by: <strong>{extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day')} days</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>•</Typography>
                    <Typography variant="body2">
                      Total after extension: <strong>{(selectedExtendApp?.extendedDays || 0) + extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day')} / 15 days</strong>
                    </Typography>
                    {(selectedExtendApp?.extendedDays || 0) + extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day') > 15 && (
                      <>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>•</Typography>
                        <Typography variant="body2" color="error">
                          <strong>Exceeds limit!</strong>
                        </Typography>
                      </>
                    )}
                  </Box>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Extension Remarks"
                value={extendedRemarks}
                onChange={(e) => setExtendedRemarks(e.target.value)}
                placeholder="Please provide reason for extending the application date..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleExtendDialogClose}
            disabled={extending}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitExtension}
            disabled={extending || !extendedDate || !extendedRemarks.trim() || (() => {
              if (!extendedDate) return false;
              const daysToExtend = extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day');
              const currentExtendedDays = selectedExtendApp?.extendedDays || 0;
              const totalDays = currentExtendedDays + daysToExtend;
              return totalDays > 15;
            })()}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              },
              '&:disabled': {
                background: '#ccc'
              }
            }}
          >
            {extending ? 'Extending...' : 'Extend Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ borderRadius: 2 }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
